import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { ScrollView, type StyleProp, type ViewStyle } from 'react-native';

// Web version of the collections ticker. reanimated's barrel export crashes in
// the web bundle (see PhysicalLightSwitch.web.tsx), so this drives the same
// continuous roll with requestAnimationFrame + scrollTo — cheap in a browser,
// where there's no JS↔native bridge to cross.

export type PromotionsTickerHandle = { pause: () => void; resume: () => void };

type Props = {
  step: number;
  count: number;
  rollMs: number;
  enabled: boolean;
  contentContainerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
};

const prefersReducedMotion = () => {
  try {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
};

const PromotionsTicker = forwardRef<PromotionsTickerHandle, Props>(({ step, count, rollMs, enabled, contentContainerStyle, style, children }, ref) => {
  const scrollRef = useRef<ScrollView>(null);
  const offsetRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number | null>(null);
  const heldRef = useRef(false);
  const userScrollRef = useRef(false);
  const canRoll = enabled && step > 0 && count > 1 && !prefersReducedMotion();
  const canRollRef = useRef(canRoll);
  canRollRef.current = canRoll;

  const stop = () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    lastFrameRef.current = null;
  };
  const start = () => {
    if (frameRef.current !== null || !canRollRef.current || heldRef.current || userScrollRef.current) return;
    const frame = (now: number) => {
      const elapsed = lastFrameRef.current === null ? 16 : Math.min(now - lastFrameRef.current, 64);
      lastFrameRef.current = now;
      let x = offsetRef.current + (step * elapsed) / rollMs;
      if (x >= (count + 1) * step) x -= count * step;
      offsetRef.current = x;
      scrollRef.current?.scrollTo({ x, animated: false });
      frameRef.current = requestAnimationFrame(frame);
    };
    frameRef.current = requestAnimationFrame(frame);
  };

  useEffect(() => {
    if (step <= 0 || count < 2) return undefined;
    offsetRef.current = step;
    const frame = requestAnimationFrame(() => scrollRef.current?.scrollTo({ x: step, animated: false }));
    return () => cancelAnimationFrame(frame);
  }, [count, step]);
  useEffect(() => {
    if (canRoll) start();
    return stop;
    // start/stop only read refs plus step/count/rollMs captured here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRoll, count, rollMs, step]);

  useImperativeHandle(ref, () => ({
    pause: () => { heldRef.current = true; stop(); },
    resume: () => { heldRef.current = false; start(); },
  }));

  const settle = (x: number) => {
    if (step <= 0) return;
    const position = Math.round(x / step);
    let target = x;
    if (position <= 0) target = count * step;
    else if (position >= count + 1) target = step;
    offsetRef.current = target;
    if (target !== x) scrollRef.current?.scrollTo({ x: target, animated: false });
    start();
  };

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      decelerationRate="fast"
      snapToInterval={step}
      snapToAlignment="start"
      disableIntervalMomentum
      contentContainerStyle={contentContainerStyle}
      scrollEventThrottle={16}
      onScroll={event => { if (frameRef.current === null) offsetRef.current = event.nativeEvent.contentOffset.x; }}
      onScrollBeginDrag={() => { userScrollRef.current = true; stop(); }}
      onScrollEndDrag={event => {
        if (event.nativeEvent.velocity?.x) return;
        userScrollRef.current = false;
        settle(event.nativeEvent.contentOffset.x);
      }}
      onMomentumScrollEnd={event => {
        if (!userScrollRef.current) return;
        userScrollRef.current = false;
        settle(event.nativeEvent.contentOffset.x);
      }}
      style={style}
    >
      {children}
    </ScrollView>
  );
});

PromotionsTicker.displayName = 'PromotionsTicker';

export default PromotionsTicker;
